import type { StaticImport } from 'next/dist/shared/lib/get-img-props'
import { THEME_ASSET_EXEMPTIONS, THEME_ASSET_PUBLIC_BASE_PATH } from 'shared/assets/constants'
import logger from 'shared/logger'

const DARK_VARIANT_INFIX = '.dark.'

const EXEMPTION_PATHS = new Set(THEME_ASSET_EXEMPTIONS)

const log = logger().child({ namespace: 'shared/assets/resolveThemeAsset' })

/** Matches Kurama `ThemeMode` without importing the design-system package. */
export type ThemeAssetMode = 'light' | 'dark'

/**
 * String URL/path, or a Next static import (`StaticImageData` / `StaticRequire`).
 * Prefer passing the import into `ThemedImage` (like `next/image`); use `.src` for
 * string-only APIs (`ThemedAvatar`, `ThemedChip`).
 */
export type ThemeAssetSrc = string | StaticImport | { src: string }

/**
 * True for string URLs/paths and Next static imports (`StaticImageData` /
 * `StaticRequire` / `{ src }`). False for React nodes and other values.
 */
export const isThemeAssetSrc = (value: unknown): value is ThemeAssetSrc => {
  if (typeof value === 'string') {
    return true
  }
  if (typeof value !== 'object' || value == null) {
    return false
  }
  if ('default' in value) {
    const defaultExport = (value as { default: unknown }).default
    return (
      typeof defaultExport === 'object' &&
      defaultExport != null &&
      'src' in defaultExport &&
      typeof (defaultExport as { src: unknown }).src === 'string'
    )
  }
  return 'src' in value && typeof (value as { src: unknown }).src === 'string'
}

export const isDarkThemeAssetPath = (relativePath: string): boolean => {
  const basename = relativePath.split('/').pop() ?? relativePath
  return basename.includes(DARK_VARIANT_INFIX)
}

export const toThemeAssetSrcString = (src: ThemeAssetSrc): string => {
  if (typeof src === 'string') {
    return src
  }
  if ('default' in src) {
    return src.default.src
  }
  return src.src
}

/**
 * Normalizes a public URL, relative path, or static-import `.src` to a path
 * relative to `public/assets/images/` (no leading slash).
 */
export const normalizeThemeAssetPath = (src: ThemeAssetSrc): string => {
  const trimmed = toThemeAssetSrcString(src).trim()

  const assetsPrefixIndex = trimmed.indexOf(`${THEME_ASSET_PUBLIC_BASE_PATH}/`)
  if (assetsPrefixIndex !== -1) {
    return trimmed.slice(assetsPrefixIndex + THEME_ASSET_PUBLIC_BASE_PATH.length + 1)
  }

  if (trimmed.startsWith('/')) {
    return trimmed.slice(1)
  }

  return trimmed
}

export const toThemeAssetPublicUrl = (relativePath: string): string => `${THEME_ASSET_PUBLIC_BASE_PATH}/${relativePath}`

/**
 * Derives the dark sibling path for a canonical asset.
 * `foo/bar.png` → `foo/bar.dark.png`
 */
export const getDarkThemeAssetPath = (canonicalRelativePath: string): string => {
  if (isDarkThemeAssetPath(canonicalRelativePath)) {
    throw new Error(`Expected a canonical (light) asset path, received dark variant: ${canonicalRelativePath}`)
  }

  const lastDot = canonicalRelativePath.lastIndexOf('.')
  if (lastDot <= 0 || lastDot === canonicalRelativePath.length - 1) {
    throw new Error(`Asset path has no extension: ${canonicalRelativePath}`)
  }

  return `${canonicalRelativePath.slice(0, lastDot)}${DARK_VARIANT_INFIX}${canonicalRelativePath.slice(lastDot + 1)}`
}

export const isThemeAssetExempt = (
  canonicalRelativePath: string,
  exemptions: readonly string[] = THEME_ASSET_EXEMPTIONS
): boolean => {
  if (exemptions === THEME_ASSET_EXEMPTIONS) {
    return EXEMPTION_PATHS.has(canonicalRelativePath)
  }
  return exemptions.includes(canonicalRelativePath)
}

export interface ResolveThemeAssetOptions {
  mode: ThemeAssetMode
  exemptions?: readonly string[]
}

/**
 * Next static-import URLs: `/_next/static/media/<name>.<hash>.<ext>`.
 * Used to recover the theme-asset relative path so dark siblings can still be
 * selected when callers pass `import img from 'public/assets/images/...'`.
 *
 * Hash shapes differ by bundler:
 * - Turbopack (Next dev): exactly 13 chars from `[a-z0-9_.~-]` (dots allowed)
 * - Webpack: hex contenthash, typically 8+ chars without dots
 */
const NEXT_STATIC_MEDIA_TURBOPACK = /^\/_next\/static\/media\/(.+)\.([a-z0-9_.~-]{13})\.([a-z0-9]+)$/i
const NEXT_STATIC_MEDIA_WEBPACK = /^\/_next\/static\/media\/(.+)\.([a-f0-9]{6,})\.([a-z0-9]+)$/i

/**
 * Recovers `name.ext` (basename relative to `public/assets/images/`) from a Next
 * static-import `.src`. Returns `null` when the URL is not a media asset path.
 * Nested import paths are flattened to the basename by Next's media URL.
 */
export const tryThemeRelativePathFromNextMediaUrl = (url: string): string | null => {
  const match = NEXT_STATIC_MEDIA_TURBOPACK.exec(url) ?? NEXT_STATIC_MEDIA_WEBPACK.exec(url)
  if (match == null) {
    return null
  }
  const [, name, , ext] = match
  return `${name}.${ext}`
}

/**
 * URLs that must never be rewritten to `/assets/images/...` siblings:
 * absolute remote/data/blob URLs (even if the path contains `/assets/images/`),
 * non-media `/_next/...` paths, and other app-absolute paths that are not under
 * the theme asset public base (e.g. `/api/assets/user_profiles/...`).
 *
 * Next `/_next/static/media/...` URLs from static imports are *not* blanket
 * pass-through: dark mode may rewrite them to a `.dark` public sibling.
 */
const isPassThroughUrl = (value: string): boolean => {
  if (/^(?:https?:|data:|blob:)/i.test(value)) {
    return true
  }
  if (value.startsWith('/_next/')) {
    // Media imports may be theme-resolved; other Next URLs pass through.
    return tryThemeRelativePathFromNextMediaUrl(value) == null
  }
  // App routes / API asset proxies — not files under public/assets/images.
  return value.startsWith('/') && !value.includes(`${THEME_ASSET_PUBLIC_BASE_PATH}/`)
}

/**
 * Returns the public URL for an asset in the given theme mode.
 * Exempt assets always resolve to the canonical (light) file.
 * Empty / nullish inputs return `''`. Remote / data / blob URLs, non-media
 * `/_next/` paths, and other non-theme absolute paths pass through unchanged
 * (including CDN paths that contain `/assets/images/`).
 *
 * Next static-import `.src` values (`/_next/static/media/<name>.<hash>.<ext>`)
 * keep the hashed URL in light mode (and when exempt) so `ThemedImage` can
 * pass `StaticImageData` through; in dark mode for non-exempt assets they
 * rewrite to the `.dark` sibling under `/assets/images/`.
 *
 * On unexpected inputs (dark variant passed as the source, extensionless path,
 * etc.) logs an error and returns the original URL so the UI does not crash.
 */
export const resolveThemeAsset = (
  src: ThemeAssetSrc | null | undefined,
  { mode, exemptions = THEME_ASSET_EXEMPTIONS }: ResolveThemeAssetOptions
): string => {
  if (src == null) {
    return ''
  }

  const raw = toThemeAssetSrcString(src).trim()
  if (raw === '' || isPassThroughUrl(raw)) {
    return raw
  }

  const nextMediaRelativePath = tryThemeRelativePathFromNextMediaUrl(raw)
  const relativePath = nextMediaRelativePath ?? normalizeThemeAssetPath(raw)

  if (isDarkThemeAssetPath(relativePath)) {
    log.error(
      { src: raw, relativePath },
      'Theme asset resolve failed: pass the canonical asset path, not a dark variant; returning original URL'
    )
    return raw
  }

  if (mode === 'dark' && !isThemeAssetExempt(relativePath, exemptions)) {
    try {
      return toThemeAssetPublicUrl(getDarkThemeAssetPath(relativePath))
    } catch (error) {
      log.error(
        {
          src: raw,
          relativePath,
          err: error instanceof Error ? error.message : String(error)
        },
        'Theme asset resolve failed; returning original URL'
      )
      return raw
    }
  }

  // Keep Next hashed media URLs so ThemedImage can pass StaticImageData through.
  if (nextMediaRelativePath != null) {
    return raw
  }

  return toThemeAssetPublicUrl(relativePath)
}
